import { ErrorCode } from '@hc-frontend/core-context';
import {
  createErrorOperationResult,
  createSuccessOperationResult,
  IOperationResult,
} from '@hc-frontend/core-utils-business-operation';
import { fetchBasedCall } from '@hc-frontend/core-utils-net';
import { z } from 'zod';

const adsAllowlistConfigResponseSchema = z.object({
  enabled: z.boolean(),
  domains: z.array(z.string()),
});

export type AdsAllowlistConfigResponse = z.infer<
  typeof adsAllowlistConfigResponseSchema
>;

export type FetchAdsAllowlistConfigResult = () => Promise<
  IOperationResult<
    AdsAllowlistConfigResponse,
    ErrorCode<'failToGetAdsAllowlistConfigFromCdn'>
  >
>;

/** Fetches the ads allowlist config from a CDN JSON file. */
export function fetchAdsAllowlistConfigFactory(
  marketplaceStaticBaseUrl: string,
): FetchAdsAllowlistConfigResult {
  return async () => {
    try {
      const adsAllowlistConfigResponse = await fetchBasedCall<
        undefined,
        unknown
      >({
        url: `${marketplaceStaticBaseUrl}/marketplace/resources/ads-allowlist-config.json`,
        method: 'GET',
      });

      if (adsAllowlistConfigResponse.result !== 'success') {
        return createErrorOperationResult({
          errors: [
            {
              code: 'failToGetAdsAllowlistConfigFromCdn',
              rootCause: new Error(
                `Ads allowlist config CDN returned ${adsAllowlistConfigResponse.status}.`,
              ),
            },
          ],
        });
      }

      const parsedResponse = adsAllowlistConfigResponseSchema.safeParse(
        adsAllowlistConfigResponse.body,
      );

      if (!parsedResponse.success) {
        return createErrorOperationResult({
          errors: [
            {
              code: 'failToGetAdsAllowlistConfigFromCdn',
              rootCause: parsedResponse.error,
            },
          ],
        });
      }

      return createSuccessOperationResult(parsedResponse.data);
    } catch (error) {
      return createErrorOperationResult({
        errors: [
          {
            code: 'failToGetAdsAllowlistConfigFromCdn',
            rootCause: error as Error,
          },
        ],
      });
    }
  };
}
